/**
 * Compression utilities for save payloads
 * Uses pako for gzip compression
 */

import pako from 'pako';

/**
 * Compress data using gzip
 * @param {Object} data - Data to compress
 * @returns {Object} - Compressed data with metadata
 */
export function compressData(data) {
  try {
    const startTime = performance.now();
    const jsonString = JSON.stringify(data);
    const originalSize = new Blob([jsonString]).size;
    
    // Only compress if data is larger than 1KB
    if (originalSize < 1024) {
      return {
        compressed: false,
        data: jsonString,
        originalSize,
        compressedSize: originalSize,
        compressionRatio: 1,
        compressionTime: 0
      };
    }
    
    // Compress using gzip
    const compressed = pako.gzip(jsonString);
    const compressedSize = compressed.length;
    const compressionTime = performance.now() - startTime;
    
    // Convert to base64 for transmission
    const base64 = btoa(String.fromCharCode.apply(null, compressed));
    
    return {
      compressed: true,
      data: base64,
      originalSize,
      compressedSize,
      compressionRatio: (originalSize / compressedSize).toFixed(2),
      compressionTime: compressionTime.toFixed(2)
    };
  } catch (error) {
    console.error('❌ Compression failed:', error);
    // Fallback to uncompressed
    return {
      compressed: false,
      data: JSON.stringify(data),
      error: error.message
    };
  }
}

/**
 * Decompress gzip data
 * @param {string} compressedData - Base64 encoded compressed data
 * @returns {Object} - Decompressed data
 */
export function decompressData(compressedData) {
  try {
    // Convert from base64
    const binaryString = atob(compressedData);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    // Decompress
    const decompressed = pako.ungzip(bytes, { to: 'string' });
    return JSON.parse(decompressed);
  } catch (error) {
    console.error('❌ Decompression failed:', error);
    throw error;
  }
}

/**
 * Check if compression would be beneficial
 * @param {Object} data - Data to check
 * @returns {boolean} - Whether to compress
 */
export function shouldCompress(data) {
  const jsonString = JSON.stringify(data);
  const size = new Blob([jsonString]).size;
  
  // Compress if larger than 1KB
  return size > 1024;
}

/**
 * Get size of data in bytes
 * @param {Object} data - Data to measure
 * @returns {number} - Size in bytes
 */
export function getDataSize(data) {
  const jsonString = JSON.stringify(data);
  return new Blob([jsonString]).size;
}

/**
 * Format bytes to human readable
 * @param {number} bytes - Bytes to format
 * @returns {string} - Formatted string
 */
export function formatBytes(bytes) {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}
